import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import SafeIcon from '../../common/SafeIcon'
import SlidePanel from './SlidePanel'
import Canvas from './Canvas'
import Toolbar from './Toolbar'
import PropertiesPanel from './PropertiesPanel'
import ProjectSettings from './ProjectSettings'
import ExportModal from './ExportModal'
import CollaborationPanel from '../Collaboration/CollaborationPanel'
import AIAssistant from '../AI/AIAssistant'
import CommentSystem from '../Comments/CommentSystem'
import VersionHistory from './VersionHistory'
import ShareModal from '../Sharing/ShareModal'
import { useSlides } from '../../hooks/useSlides'
import { formatDistanceToNow } from 'date-fns'
import * as FiIcons from 'react-icons/fi'

const { 
  FiSave, FiPlay, FiShare2, FiSettings, FiArrowLeft, FiDownload, 
  FiUsers, FiZap, FiMessageSquare, FiClock, FiMoreHorizontal,
  FiRotateCcw, FiRotateCw
} = FiIcons

const SlideEditor = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [project, setProject] = useState(null)
  const [slides, setSlides] = useState([])
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [selectedElement, setSelectedElement] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState(null)
  const saveTimeoutRef = useRef(null)
  const [showSettings, setShowSettings] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showCollaboration, setShowCollaboration] = useState(false)
  const [showAIAssistant, setShowAIAssistant] = useState(false)
  const [showVersionHistory, setShowVersionHistory] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [gridEnabled, setGridEnabled] = useState(false)
  const [snapEnabled, setSnapEnabled] = useState(true)
  
  // Undo system state
  const [undoStack, setUndoStack] = useState([])
  const [redoStack, setRedoStack] = useState([])
  const maxUndoStackSize = 50
  const [commentSystem, setCommentSystem] = useState({
    isVisible: false,
    position: null,
    slideId: null,
    elementId: null
  })
  const [isEditingProjectTitle, setIsEditingProjectTitle] = useState(false)
  const [projectTitleValue, setProjectTitleValue] = useState('')

  // Load slides from database
  const { slides: dbSlides, loading: slidesLoading } = useSlides(id)

  useEffect(() => {
    const loadProject = async () => {
      if (!id) return
      
      try {
        const { supabase } = await import('../../lib/supabase')
        
        // Load project from database
        const { data: projectData, error } = await supabase
          .from('projects')
          .select('*')
          .eq('id', id)
          .single()

        if (error) {
          console.error('Error loading project:', error)
          // Fallback to localStorage for existing projects
          const savedProject = localStorage.getItem(`nupitch_project_${id}`)
      if (savedProject) {
        const projectData = JSON.parse(savedProject)
        setProject({
          id: id,
          title: projectData.title,
          status: 'draft',
          createdAt: projectData.createdAt,
          updatedAt: projectData.updatedAt,
          thumbnail: null
        })
        setSlides(projectData.slides)
          }
          setLoading(false)
          return
        }

        setProject(projectData)
        setProjectTitleValue(projectData.title || '')
      } catch (error) {
        console.error('Error loading project:', error)
      }
      
      setLoading(false)
    }

    loadProject()
  }, [id])

  useEffect(() => {
    console.log('🎯 dbSlides changed:', dbSlides?.length || 0, 'slides loaded')
    if (dbSlides && dbSlides.length > 0) {
      console.log('✅ Setting slides from database:', dbSlides)
      
      // Transform database format to UI format
      const transformedSlides = dbSlides.map(slide => ({
        ...slide,
        title: slide.metadata?.name || slide.title || `Slide ${slide.slide_order + 1}`, // Use metadata name first
        elements: (slide.elements || []).map(element => ({
          ...element,
          type: element.element_type, // Transform element_type to type
          // Handle different element types properly
          ...(element.element_type === 'text' && {
            content: (() => {
              // Handle text content transformation robustly
              if (typeof element.content === 'string') {
                return element.content
              } else if (element.content?.text) {
                if (typeof element.content.text === 'string') {
                  return element.content.text
                } else if (Array.isArray(element.content.text)) {
                  return element.content.text.join(' ')
                } else {
                  return String(element.content.text)
                }
              } else if (typeof element.content === 'object' && element.content !== null) {
                // If content is an object but doesn't have a text property, return empty string
                // to prevent [object Object] from appearing
                console.warn('⚠️ Text element with object content but no text property:', element.id, element.content)
                return ''
              } else {
                return element.content ? String(element.content) : ''
              }
            })(),
          }),
          ...(element.element_type === 'image' && {
            src: element.content?.src || element.src || 'https://images.unsplash.com/photo-1566438480900-0609be27a4be?w=400&h=300&fit=crop', // Extract image src
            content: '' // Images don't need content
          }),
          ...(element.element_type === 'list' && {
            content: (() => {
              console.log('🔍 Debug list element:', element.id, 'content:', element.content, 'items:', element.content?.items, 'text:', element.content?.text)
              
              // Handle different list content formats
              if (element.content?.items && Array.isArray(element.content.items)) {
                return element.content.items
              } else if (element.content?.text && Array.isArray(element.content.text)) {
                // Handle legacy format where list items are stored as {text: [...]}
                return element.content.text
              } else if (Array.isArray(element.content)) {
                return element.content
              } else if (typeof element.content === 'string') {
                try {
                  return element.content.split(',').map(item => item.trim())
                } catch (e) {
                  return [element.content]
                }
              } else {
                console.warn('⚠️ Unknown list content format:', element.content)
                return []
              }
            })(),
          }),
          ...(element.element_type === 'video' && {
            videoUrl: element.content?.videoUrl || element.videoUrl || 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', // Extract video URL
            autoplay: element.content?.autoplay || element.autoplay || false, // Extract autoplay setting
            content: '' // Videos don't need content string
          }),
          ...(element.element_type === 'shape' && {
            content: '', // Shapes don't need content
            shape: element.content?.shape || element.shape || 'rectangle' // Extract shape type
          })
        }))
      }))
      
      // Debug: Check what element IDs we're getting from database
      transformedSlides.forEach((slide, i) => {
        console.log(`Slide ${i+1}:`, slide.id, 'Elements:', slide.elements?.map(el => `${el.id} (${el.type}): "${el.content}"`))
      })
      
      setSlides(transformedSlides)
    } else {
      console.log('⚠️ No slides found in database for project:', id)
    }
  }, [dbSlides])

  useEffect(() => {
    console.log('📊 slidesLoading:', slidesLoading)
    if (slidesLoading !== undefined) {
      setLoading(slidesLoading)
    }
  }, [slidesLoading])

  const handleSave = async () => {
    setSaving(true)
    
    try {
      const { supabase } = await import('../../lib/supabase')
      
      // Update project timestamp
      await supabase
        .from('projects')
        .update({ updated_at: new Date() })
        .eq('id', id)
      
      console.log('Project saved successfully')
    } catch (error) {
      console.error('Error saving project:', error)
    }
    
    setTimeout(() => {
      setSaving(false)
    }, 1000)
  }

  const handlePreview = () => {
    navigate(`/present/${id}`)
  }

  const handleShare = () => {
    setShowShareModal(true)
  }

  const handleProjectUpdate = (updatedProject) => {
    console.log('🔄 Updating project data after share settings save:', updatedProject)
    setProject(updatedProject)
  }

  // Undo system functions
  const createSnapshot = useCallback(() => {
    return {
      slides: JSON.parse(JSON.stringify(slides)),
      activeSlideIndex,
      selectedElement,
      timestamp: Date.now()
    }
  }, [slides, activeSlideIndex, selectedElement])

  const pushToUndoStack = useCallback((snapshot) => {
    setUndoStack(prev => {
      const newStack = [...prev, snapshot]
      // Limit stack size to prevent memory issues
      if (newStack.length > maxUndoStackSize) {
        return newStack.slice(-maxUndoStackSize)
      }
      return newStack
    })
    // Clear redo stack when new action is performed
    setRedoStack([])
  }, [maxUndoStackSize])

  const handleDragEnd = useCallback(() => {
    // Take snapshot after drag/resize/rotate operations complete
    const snapshot = createSnapshot()
    pushToUndoStack(snapshot)
  }, [createSnapshot, pushToUndoStack])

  const undo = useCallback(() => {
    if (undoStack.length === 0) return

    const currentSnapshot = createSnapshot()
    const previousSnapshot = undoStack[undoStack.length - 1]

    // Move current state to redo stack
    setRedoStack(prev => [...prev, currentSnapshot])
    
    // Remove last item from undo stack
    setUndoStack(prev => prev.slice(0, -1))
    
    // Restore previous state
    setSlides(previousSnapshot.slides)
    setActiveSlideIndex(previousSnapshot.activeSlideIndex)
    setSelectedElement(previousSnapshot.selectedElement)
    
    console.log('↶ Undo applied')
  }, [undoStack, createSnapshot])

  const redo = useCallback(() => {
    if (redoStack.length === 0) return

    const currentSnapshot = createSnapshot()
    const nextSnapshot = redoStack[redoStack.length - 1]

    // Move current state to undo stack
    setUndoStack(prev => [...prev, currentSnapshot])
    
    // Remove last item from redo stack
    setRedoStack(prev => prev.slice(0, -1))
    
    // Restore next state
    setSlides(nextSnapshot.slides)
    setActiveSlideIndex(nextSnapshot.activeSlideIndex)
    setSelectedElement(nextSnapshot.selectedElement)
    
    console.log('↷ Redo applied')
  }, [redoStack, createSnapshot])

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if user is typing in an input field
      const isTyping = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.contentEditable === 'true'
      if (isTyping) return

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey

      if (ctrlOrCmd && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        undo()
      } else if (ctrlOrCmd && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault()
        redo()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [undo, redo])

  const handleElementRightClick = (e, elementId) => {
    e.preventDefault()
    const rect = e.currentTarget.getBoundingClientRect()
    setCommentSystem({
      isVisible: true,
      position: { x: rect.left + rect.width / 2, y: rect.top },
      slideId: slides[activeSlideIndex].id,
      elementId
    })
  }

  const addSlide = async (type = 'content') => {
    // Generate UUID for database compatibility
    const generateUUID = () => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    }

    const slideId = generateUUID()
    const newSlide = {
      id: slideId,
      type,
      elements: [],
      background: {
        type: 'color',
        value: '#ffffff'
      }
    }
    
    const insertPosition = activeSlideIndex + 1
    
    // Update local state immediately for responsive UI
    const newSlides = [...slides]
    newSlides.splice(insertPosition, 0, newSlide)
    setSlides(newSlides)
    setActiveSlideIndex(insertPosition)

    // Save new slide to database and update order of all slides
    try {
      const { supabase } = await import('../../lib/supabase')
      
      console.log('🆕 Adding new slide at position', insertPosition)
      
      // STEP 1: First, shift existing slides to make room for the new slide
      // Move all slides at insertPosition and after to temporary high values
      console.log('🔧 Step 1: Moving existing slides to temporary positions...')
      const slidesToShift = slides.slice(insertPosition)
      for (let i = 0; i < slidesToShift.length; i++) {
        const { error } = await supabase
          .from('slides')
          .update({ slide_order: 2000 + i })
          .eq('id', slidesToShift[i].id)
        
        if (error) {
          console.log(`❌ Failed to move slide ${slidesToShift[i].id} to temp position:`, error)
          throw error
        }
      }
      
      // STEP 2: Insert the new slide at the desired position
      console.log('🔧 Step 2: Inserting new slide...')
      const { error: insertError } = await supabase
        .from('slides')
        .insert({
          id: slideId,
          project_id: id,
          slide_order: insertPosition,
          slide_type: type,
          background: newSlide.background,
          metadata: { type: type, name: `Slide ${insertPosition + 1}` }
        })

      if (insertError) {
        console.error('❌ Insert error details:', insertError)
        throw insertError
      }

      // STEP 3: Update all slides to their final correct positions
      console.log('🔧 Step 3: Setting final positions for all slides...')
      for (let i = 0; i < newSlides.length; i++) {
        const { error } = await supabase
          .from('slides')
          .update({ 
            slide_order: i
          })
          .eq('id', newSlides[i].id)
        
        if (error) {
          console.log(`❌ Failed to set final position for slide ${newSlides[i].id}:`, error)
          throw new Error(`Failed to update slide order: ${error.message}`)
        } else {
          console.log(`✅ Updated slide ${newSlides[i].id} to order ${i}`)
        }
      }

      console.log('✅ New slide saved and order updated successfully')
    } catch (error) {
      console.error('❌ Error in addSlide:', error)
      alert('Failed to save slide. Please try again.')
      
      // Revert local state
      const revertedSlides = [...slides]
      revertedSlides.splice(insertPosition, 1)
      setSlides(revertedSlides)
      setActiveSlideIndex(Math.max(0, insertPosition - 1))
    }
  }

  const deleteSlide = async (index) => {
    if (slides.length <= 1) return
    
    const slideToDelete = slides[index]
    const newSlides = slides.filter((_, i) => i !== index)
    setSlides(newSlides)
    
    if (activeSlideIndex >= newSlides.length) {
      setActiveSlideIndex(newSlides.length - 1)
    }

    // Delete from database and update order
    try {
      const { supabase } = await import('../../lib/supabase')
      
      console.log('🗑️ Deleting slide:', slideToDelete.id)
      
      // Delete the slide
      const { error: deleteError } = await supabase
        .from('slides')
        .delete()
        .eq('id', slideToDelete.id)

      if (deleteError) {
        throw deleteError
      }

      // Update the slide_order of remaining slides to maintain correct order
      const updatePromises = newSlides.map((slide, index) => {
        console.log(`  - Updating slide ${slide.id} to order ${index}`)
        return supabase
          .from('slides')
          .update({ slide_order: index })
          .eq('id', slide.id)
      })
      
      const results = await Promise.all(updatePromises)
      const errors = results.filter(result => result.error)
      
      if (errors.length > 0) {
        throw new Error('Failed to update slide orders: ' + errors[0].error.message)
      }

      console.log('✅ Slide deleted and order updated successfully')
    } catch (error) {
      console.error('❌ Error deleting slide:', error)
      // Could revert state here, but slide is already deleted locally
    }
  }

  const duplicateSlide = async (index) => {
    const slideToClone = slides[index]
    
    // Generate UUID for database compatibility
    const generateUUID = () => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    }

    const newSlideId = generateUUID()
    const newSlide = {
      ...slideToClone,
      id: newSlideId,
      elements: slideToClone.elements.map(el => ({
        ...el,
        id: generateUUID()
      }))
    }
    
    const insertPosition = index + 1
    const newSlides = [...slides]
    newSlides.splice(insertPosition, 0, newSlide)
    setSlides(newSlides)
    setActiveSlideIndex(insertPosition)

    // Save duplicated slide to database using the same pattern as addSlide
    try {
      const { supabase } = await import('../../lib/supabase')
      
      console.log('📋 Duplicating slide at position', insertPosition)
      
      // STEP 1: First, shift existing slides to make room for the new slide
      // Move all slides at insertPosition and after to temporary high values
      console.log('🔧 Step 1: Moving existing slides to temporary positions...')
      const slidesToShift = slides.slice(insertPosition)
      for (let i = 0; i < slidesToShift.length; i++) {
        const { error } = await supabase
          .from('slides')
          .update({ slide_order: 2000 + i })
          .eq('id', slidesToShift[i].id)
        
        if (error) {
          console.log(`❌ Failed to move slide ${slidesToShift[i].id} to temp position:`, error)
          throw error
        }
      }
      
      // STEP 2: Insert the new slide at the desired position
      console.log('🔧 Step 2: Inserting new slide...')
      const { error: insertError } = await supabase
        .from('slides')
        .insert({
          id: newSlideId,
          project_id: id,
          slide_order: insertPosition,
          slide_type: slideToClone.slide_type || 'content',
          background: slideToClone.background,
          metadata: slideToClone.metadata || { type: 'content' }
        })

      if (insertError) {
        console.error('❌ Insert error details:', insertError)
        throw insertError
      }

      // STEP 3: Update all slides to their final correct positions
      console.log('🔧 Step 3: Setting final positions for all slides...')
      for (let i = 0; i < newSlides.length; i++) {
        const { error } = await supabase
          .from('slides')
          .update({ 
            slide_order: i
          })
          .eq('id', newSlides[i].id)
        
        if (error) {
          console.log(`❌ Failed to set final position for slide ${newSlides[i].id}:`, error)
          throw new Error(`Failed to update slide order: ${error.message}`)
        } else {
          console.log(`✅ Updated slide ${newSlides[i].id} to order ${i}`)
        }
      }

      // STEP 4: Create elements for the duplicated slide
      if (newSlide.elements && newSlide.elements.length > 0) {
        console.log('🔧 Step 4: Creating elements for duplicated slide...')
        const elementsToCreate = newSlide.elements.map(element => ({
          id: element.id,
          slide_id: newSlideId,
          element_type: element.type,
          content: element.type === 'text' ? { text: element.content } :
                   element.type === 'image' ? { src: element.src } :
                   element.type === 'video' ? { videoUrl: element.videoUrl, autoplay: element.autoplay } :
                   element.type === 'list' ? { items: element.content } :
                   { data: element.content },
          style: element.style,
          position: element.style?.position || { x: 0, y: 0 }
        }))

        const { error: elementsError } = await supabase
          .from('elements')
          .insert(elementsToCreate)

        if (elementsError) {
          throw elementsError
        }

        console.log('✅ Created elements for duplicated slide')
      }

      console.log('✅ Slide duplicated successfully')
    } catch (error) {
      console.error('❌ Error duplicating slide:', error)
      alert('Failed to duplicate slide. Please try again.')
      
      // Revert local state by removing the duplicated slide that was temporarily added
      const revertedSlides = [...newSlides]
      revertedSlides.splice(insertPosition, 1) // Remove the newly added slide
      setSlides(revertedSlides)
      setActiveSlideIndex(index) // Go back to the original slide
    }
  }

  const reorderSlides = async (oldIndex, newIndex) => {
    console.log('🔄 Reordering slides from index', oldIndex, 'to', newIndex)
    
    const newSlides = [...slides]
    const [removed] = newSlides.splice(oldIndex, 1)
    newSlides.splice(newIndex, 0, removed)
    setSlides(newSlides)
    
    console.log('📊 Slides after reorder:', newSlides.map((slide, i) => `${i}: ${slide.id}`))
    
    if (activeSlideIndex === oldIndex) {
      setActiveSlideIndex(newIndex)
    } else if (activeSlideIndex > oldIndex && activeSlideIndex <= newIndex) {
      setActiveSlideIndex(activeSlideIndex - 1)
    } else if (activeSlideIndex < oldIndex && activeSlideIndex >= newIndex) {
      setActiveSlideIndex(activeSlideIndex + 1)
    }

    // Save the new order to database
    try {
      const { supabase } = await import('../../lib/supabase')
      
      console.log('💾 Saving slide order to database...')
      
      // Update slide_order sequentially to avoid conflicts
      // First, set all slides to temporary high order values to avoid conflicts
      console.log('🔧 Setting temporary order values...')
      for (let i = 0; i < newSlides.length; i++) {
        const tempOrder = 1000 + i  // Use high numbers to avoid conflicts
        const { error } = await supabase
          .from('slides')
          .update({ slide_order: tempOrder })
          .eq('id', newSlides[i].id)
        
        if (error) {
          console.log(`❌ Failed to set temp order for slide ${newSlides[i].id}:`, error)
        }
      }
      
      // Then update to final order values
      console.log('🔧 Setting final order values...')
      for (let i = 0; i < newSlides.length; i++) {
        const { error } = await supabase
          .from('slides')
          .update({ slide_order: i })
          .eq('id', newSlides[i].id)
        
        if (error) {
          console.log(`❌ Failed to set final order for slide ${newSlides[i].id}:`, error)
        } else {
          console.log(`✅ Updated slide ${newSlides[i].id} to order ${i}`)
        }
      }
    } catch (error) {
      console.error('❌ Error saving slide order to database:', error)
      // Could show user feedback here, but slides are already reordered locally
    }
  }

  const updateSlide = async (slideIndex, updates) => {
    const newSlides = [...slides]
    newSlides[slideIndex] = { ...newSlides[slideIndex], ...updates }
    setSlides(newSlides)

    // Save to database
    try {
      const { supabase } = await import('../../lib/supabase')
      const slide = newSlides[slideIndex]
      
      await supabase
        .from('slides')
        .update({
          title: slide.title,
          background: slide.background,
          metadata: slide.metadata,
          updated_at: new Date()
        })
        .eq('id', slide.id)
    } catch (error) {
      console.error('Error updating slide in database:', error)
    }
  }

  const updateSlideTitle = async (slideIndex, newTitle) => {
    const newSlides = [...slides]
    const slide = newSlides[slideIndex]
    
    // Update title in local state
    newSlides[slideIndex] = { 
      ...slide, 
      title: newTitle,
      metadata: { ...slide.metadata, name: newTitle }
    }
    setSlides(newSlides)

    // Save to database in metadata field
    try {
      const { supabase } = await import('../../lib/supabase')
      
      await supabase
        .from('slides')
        .update({ 
          metadata: { ...slide.metadata, name: newTitle }
        })
        .eq('id', slide.id)
      
      console.log('✅ Slide title updated in metadata:', newTitle)
    } catch (error) {
      console.error('Error updating slide title:', error)
    }
  }

  // Layer management functions
  const bringToFront = (elementId) => {
    const updatedSlides = [...slides]
    const slide = updatedSlides[activeSlideIndex]
    const allZIndexes = slide.elements.map(el => el.style.zIndex || 0)
    const maxZIndex = allZIndexes.length > 0 ? Math.max(...allZIndexes) : 0
    
    const elementIndex = slide.elements.findIndex(el => el.id === elementId)
    if (elementIndex !== -1) {
      slide.elements[elementIndex].style.zIndex = maxZIndex + 1
      setSlides(updatedSlides)
      
      // Save to database
      updateElement(elementId, { style: slide.elements[elementIndex].style })
    }
  }

  const sendToBack = (elementId) => {
    const updatedSlides = [...slides]
    const slide = updatedSlides[activeSlideIndex]
    const allZIndexes = slide.elements.map(el => el.style.zIndex || 0)
    const minZIndex = allZIndexes.length > 0 ? Math.min(...allZIndexes) : 0
    
    const elementIndex = slide.elements.findIndex(el => el.id === elementId)
    if (elementIndex !== -1) {
      // Ensure minimum z-index is 1 (never go behind background)
      const newZIndex = Math.max(1, minZIndex - 1)
      slide.elements[elementIndex].style.zIndex = newZIndex
      setSlides(updatedSlides)
      
      // Save to database
      updateElement(elementId, { style: slide.elements[elementIndex].style })
    }
  }

  const bringForward = (elementId) => {
    const updatedSlides = [...slides]
    const slide = updatedSlides[activeSlideIndex]
    
    const elementIndex = slide.elements.findIndex(el => el.id === elementId)
    if (elementIndex !== -1) {
      const currentElement = slide.elements[elementIndex]
      const currentZIndex = currentElement.style.zIndex || 0
      
      // Find the next higher z-index to swap with
      const higherElements = slide.elements
        .filter(el => el.id !== elementId && (el.style.zIndex || 0) > currentZIndex)
        .sort((a, b) => (a.style.zIndex || 0) - (b.style.zIndex || 0))
      
      if (higherElements.length > 0) {
        const nextHigherElement = higherElements[0]
        const nextZIndex = nextHigherElement.style.zIndex || 0
        
        // Swap z-indexes
        currentElement.style.zIndex = nextZIndex
        const nextElementIndex = slide.elements.findIndex(el => el.id === nextHigherElement.id)
        slide.elements[nextElementIndex].style.zIndex = currentZIndex
        
        setSlides(updatedSlides)
        
        // Save both elements to database
        updateElement(elementId, { style: currentElement.style })
        updateElement(nextHigherElement.id, { style: slide.elements[nextElementIndex].style })
      }
    }
  }

  const sendBackward = (elementId) => {
    const updatedSlides = [...slides]
    const slide = updatedSlides[activeSlideIndex]
    
    const elementIndex = slide.elements.findIndex(el => el.id === elementId)
    if (elementIndex !== -1) {
      const currentElement = slide.elements[elementIndex]
      const currentZIndex = currentElement.style.zIndex || 0
      
      // Find the next lower z-index to swap with (but ensure it stays >= 1)
      const lowerElements = slide.elements
        .filter(el => el.id !== elementId && (el.style.zIndex || 0) < currentZIndex && (el.style.zIndex || 0) >= 1)
        .sort((a, b) => (b.style.zIndex || 0) - (a.style.zIndex || 0))
      
      if (lowerElements.length > 0) {
        const nextLowerElement = lowerElements[0]
        const nextZIndex = nextLowerElement.style.zIndex || 0
        
        // Swap z-indexes (ensure minimum of 1)
        currentElement.style.zIndex = Math.max(1, nextZIndex)
        const nextElementIndex = slide.elements.findIndex(el => el.id === nextLowerElement.id)
        slide.elements[nextElementIndex].style.zIndex = currentZIndex
        
        setSlides(updatedSlides)
        
        // Save both elements to database
        updateElement(elementId, { style: currentElement.style })
        updateElement(nextLowerElement.id, { style: slide.elements[nextElementIndex].style })
      }
    }
  }

  const addElement = async (type) => {
    // Take snapshot before making changes
    const snapshot = createSnapshot()
    pushToUndoStack(snapshot)
    
    // Generate UUID for database compatibility
    const generateUUID = () => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    }

    const elementId = generateUUID()
    console.log('🆔 Generated element ID:', elementId)
    
    const currentSlide = slides[activeSlideIndex]
    console.log('📝 Current slide:', currentSlide?.id, 'activeSlideIndex:', activeSlideIndex)
    
    if (!currentSlide || !currentSlide.id) {
      console.error('❌ Cannot add element: no valid slide found')
      alert('Cannot add element: no slide selected')
      return
    }
    
    const newElement = {
      id: elementId,
      type,
      content: type === 'text' ? 'New Text' : type === 'list' ? ['List item 1', 'List item 2'] : '',
      style: {
        fontSize: '18px',
        color: '#374151',
        position: { x: 100, y: 100 },
        width: type === 'image' || type === 'video' ? 300 : 400,
        height: type === 'image' || type === 'video' ? 200 : type === 'text' ? 50 : 100,
        rotation: 0, // Add rotation support
        zIndex: Math.max(1, currentSlide.elements.length + 1) // Auto-assign z-index starting from 1
      }
    }

    if (type === 'image') {
      newElement.src = 'https://images.unsplash.com/photo-1566438480900-0609be27a4be?w=400&h=300&fit=crop'
    }

    if (type === 'video') {
      newElement.videoUrl = 'https://www.youtube.com/watch?v=dQw4w9WgXcQ' // Default YouTube URL
      newElement.autoplay = false // Default to not autoplay
    }

    if (type === 'shape') {
      newElement.style = {
        ...newElement.style,
        backgroundColor: '#3b82f6',
        borderRadius: '0px',
        shapeType: 'rectangle'
      }
    }

    // Update local state immediately for responsive UI
    const updatedSlides = [...slides]
    updatedSlides[activeSlideIndex].elements.push(newElement)
    setSlides(updatedSlides)
    setSelectedElement(newElement.id)

    // Save new element to database
    try {
      const { supabase } = await import('../../lib/supabase')
      
      // Prepare content based on element type
      let contentToSave = {}
      if (type === 'text') {
        contentToSave = { text: newElement.content }
      } else if (type === 'image') {
        contentToSave = { src: newElement.src }
      } else if (type === 'video') {
        contentToSave = { videoUrl: newElement.videoUrl, autoplay: newElement.autoplay }
      } else if (type === 'list') {
        contentToSave = { items: newElement.content }
      } else {
        contentToSave = { data: newElement.content }
      }

      console.log('💾 Attempting to save element:', {
        id: elementId,
        slide_id: currentSlide.id,
        type: type,
        content: contentToSave
      })
      
      const { error } = await supabase
        .from('elements')
        .insert({
          id: elementId,
          slide_id: currentSlide.id,
          element_type: type,
          content: contentToSave,
          style: newElement.style,
          position: newElement.style.position
        })

      if (error) {
        console.error('❌ Error saving new element to database:', error)
        console.error('Error details:', error.message, error.details, error.hint)
        // Show user feedback for failed save
        alert('Failed to save element. Please try again.')
        // Revert local state
        const revertedSlides = [...slides]
        revertedSlides[activeSlideIndex].elements = revertedSlides[activeSlideIndex].elements.filter(el => el.id !== elementId)
        setSlides(revertedSlides)
        setSelectedElement(null)
      } else {
        console.log('✅ New element saved to database:', elementId)
      }
    } catch (error) {
      console.error('💥 Exception in addElement database save:', error)
      alert('Failed to save element. Please try again.')
    }
  }

  // Debounced save function
  const debouncedSave = useCallback((saveFunction) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    
    setSaving(true)
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await saveFunction()
        setLastSaved(new Date())
        console.log('✅ Auto-save completed')
      } catch (error) {
        console.error('❌ Auto-save failed:', error)
      } finally {
        setSaving(false)
      }
    }, 1000) // 1 second debounce
  }, [])

  const updateElement = async (elementId, updates, shouldSnapshot = false) => {
    console.log('🔄 updateElement called with ID:', elementId, 'updates:', updates)
    
    // Take snapshot for significant changes (like position changes after drag)
    if (shouldSnapshot) {
      const snapshot = createSnapshot()
      pushToUndoStack(snapshot)
    }
    
    const updatedSlides = [...slides]
    const slide = updatedSlides[activeSlideIndex]
    const elementIndex = slide.elements.findIndex(el => el.id === elementId)
    
    if (elementIndex !== -1) {
      // Apply updates to the element
      const currentElement = slide.elements[elementIndex]
      slide.elements[elementIndex] = { ...currentElement, ...updates }
      setSlides(updatedSlides)

      // Check if this is an old-style ID before attempting to save
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(elementId)
      
      if (!isUUID) {
        console.warn('⚠️ Skipping database save for old-style element ID:', elementId)
        return // Don't try to save old-style IDs to database
      }

      // Capture element data before debounced save to avoid race conditions
      const elementToSave = slide.elements[elementIndex]
      
      // Debounced save to database
      debouncedSave(async () => {
        const { supabase } = await import('../../lib/supabase')
        
        console.log('💾 Saving element update to database:', elementId, 'element:', elementToSave)
        
        // Prepare content based on element type - merge with existing content
        let contentToSave = {}
        
        if (elementToSave.type === 'text') {
          contentToSave = { text: elementToSave.content || '' }
        } else if (elementToSave.type === 'image') {
          // For images, merge the src property into content
          contentToSave = { src: elementToSave.src || '' }
        } else if (elementToSave.type === 'video') {
          // For videos, merge the videoUrl property into content
          contentToSave = { videoUrl: elementToSave.videoUrl || '', autoplay: elementToSave.autoplay || false }
        } else if (elementToSave.type === 'list') {
          contentToSave = { items: Array.isArray(elementToSave.content) ? elementToSave.content : [] }
        } else if (elementToSave.type === 'shape') {
          contentToSave = { shape: elementToSave.shape || 'rectangle' }
        } else {
          contentToSave = { data: elementToSave.content || '' }
        }

        console.log('💾 Content to save:', contentToSave)

        const { error } = await supabase
          .from('elements')
          .update({
            content: contentToSave,
            style: elementToSave.style,
            position: elementToSave.style?.position || { x: 0, y: 0 },
            updated_at: new Date()
          })
          .eq('id', elementId)
          
        if (error) {
          console.error('❌ Error updating element in database:', error)
          throw error
        }
        
        console.log('✅ Element updated in database:', elementId)
      })
    }
  }

  const deleteElement = async (elementId) => {
    console.log('🗑️ Deleting element:', elementId)
    
    // Take snapshot before making changes
    const snapshot = createSnapshot()
    pushToUndoStack(snapshot)
    
    const updatedSlides = [...slides]
    const slide = updatedSlides[activeSlideIndex]
    slide.elements = slide.elements.filter(el => el.id !== elementId)
    setSlides(updatedSlides)
    setSelectedElement(null)

    // Check if this is a UUID before attempting to delete from database
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(elementId)
    
    if (!isUUID) {
      console.warn('⚠️ Skipping database deletion for old-style element ID:', elementId)
      return // Don't try to delete old-style IDs from database
    }

    // Delete from database
    try {
      const { supabase } = await import('../../lib/supabase')
      
      const { error } = await supabase
        .from('elements')
        .delete()
        .eq('id', elementId)
        
      if (error) {
        console.error('❌ Error deleting element from database:', error)
        // Could revert local state here, but element is already deleted
      } else {
        console.log('✅ Element deleted from database:', elementId)
      }
    } catch (error) {
      console.error('❌ Exception deleting element from database:', error)
    }
  }

  const duplicateElement = async (elementId) => {
    console.log('🔄 Duplicating element:', elementId)
    
    const updatedSlides = [...slides]
    const slide = updatedSlides[activeSlideIndex]
    const elementIndex = slide.elements.findIndex(el => el.id === elementId)
    
    if (elementIndex !== -1) {
      const originalElement = slide.elements[elementIndex]
      
      // Generate UUID for database compatibility
      const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = Math.random() * 16 | 0;
          const v = c == 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      }

      const newElementId = generateUUID()
      
      // Create duplicated element with slight offset and higher z-index
      const duplicatedElement = {
        ...originalElement,
        id: newElementId,
        style: {
          ...originalElement.style,
          position: {
            x: Math.min(800 - originalElement.style.width, originalElement.style.position.x + 20),
            y: Math.min(600 - originalElement.style.height, originalElement.style.position.y + 20)
          },
          zIndex: Math.max(1, (originalElement.style.zIndex || 0) + 1)
        }
      }

      // Add to local state
      slide.elements.push(duplicatedElement)
      setSlides(updatedSlides)
      setSelectedElement(newElementId)

      // Save to database
      try {
        const { supabase } = await import('../../lib/supabase')

        // Prepare content based on element type
        let contentToSave = {}
        
        if (duplicatedElement.type === 'text') {
          contentToSave = { text: duplicatedElement.content || '' }
        } else if (duplicatedElement.type === 'image') {
          contentToSave = { src: duplicatedElement.src || '' }
        } else if (duplicatedElement.type === 'video') {
          contentToSave = { videoUrl: duplicatedElement.videoUrl || '', autoplay: duplicatedElement.autoplay || false }
        } else if (duplicatedElement.type === 'list') {
          contentToSave = { items: Array.isArray(duplicatedElement.content) ? duplicatedElement.content : [] }
        } else if (duplicatedElement.type === 'shape') {
          contentToSave = { shape: duplicatedElement.shape || 'rectangle' }
        } else {
          contentToSave = { data: duplicatedElement.content || '' }
        }

        const { error } = await supabase
          .from('elements')
          .insert({
            id: newElementId,
            slide_id: slide.id,
            element_type: duplicatedElement.type,
            content: contentToSave,
            style: duplicatedElement.style,
            position: duplicatedElement.style.position,
            created_at: new Date(),
            updated_at: new Date()
          })

        if (error) {
          console.error('❌ Error saving duplicated element to database:', error)
          // Revert local state on error
          const revertedSlides = [...slides]
          const revertedSlide = revertedSlides[activeSlideIndex]
          revertedSlide.elements = revertedSlide.elements.filter(el => el.id !== newElementId)
          setSlides(revertedSlides)
          setSelectedElement(null)
          alert('Failed to duplicate element. Please try again.')
          return
        }

        console.log('✅ Element duplicated successfully:', newElementId)
      } catch (error) {
        console.error('❌ Error duplicating element:', error)
        alert('Failed to duplicate element. Please try again.')
      }
    }
  }

  const alignElements = (alignment) => {
    if (!selectedElement) return
    
    const slide = slides[activeSlideIndex]
    const element = slide.elements.find(el => el.id === selectedElement)
    if (!element) return

    let newX = element.style.position.x

    switch (alignment) {
      case 'left':
        newX = 50
        break
      case 'center':
        newX = (800 - element.style.width) / 2
        break
      case 'right':
        newX = 800 - element.style.width - 50
        break
    }

    updateElement(selectedElement, {
      style: {
        ...element.style,
        position: { ...element.style.position, x: newX }
      }
    })
  }

  const applyBackgroundToAll = () => {
    const currentBackground = slides[activeSlideIndex].background
    const updatedSlides = slides.map(slide => ({
      ...slide,
      background: { ...currentBackground }
    }))
    setSlides(updatedSlides)
    alert('Background applied to all slides!')
  }

  const updateProject = (updates) => {
    setProject(prev => ({ ...prev, ...updates }))
  }

  const updateProjectTitle = async (newTitle) => {
    if (!newTitle.trim() || newTitle === project?.title) return

    // Update local state immediately
    setProject(prev => ({ ...prev, title: newTitle }))
    setProjectTitleValue(newTitle)

    // Save to database
    try {
      const { supabase } = await import('../../lib/supabase')
      
      const { error } = await supabase
        .from('projects')
        .update({ title: newTitle })
        .eq('id', id)

      if (error) {
        console.error('Error updating project title:', error)
        // Revert on error
        setProject(prev => ({ ...prev, title: project?.title }))
        setProjectTitleValue(project?.title || '')
        alert('Failed to update project title. Please try again.')
      } else {
        console.log('✅ Project title updated:', newTitle)
      }
    } catch (error) {
      console.error('Error updating project title:', error)
      // Revert on error
      setProject(prev => ({ ...prev, title: project?.title }))
      setProjectTitleValue(project?.title || '')
    }
  }

  const handleRestoreVersion = (version) => {
    alert(`Restored to version ${version.id}`)
    setShowVersionHistory(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading editor...</p>
        </div>
      </div>
    )
  }

  const currentSlide = slides[activeSlideIndex]



  // Don't render editor if no slides loaded yet
  if (!currentSlide && slides.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading slides...</p>
        </div>
      </div>
    )
  }

  // If we have slides but no current slide, reset to first slide
  if (!currentSlide && slides.length > 0) {
    setActiveSlideIndex(0)
    return null
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        slides={slides}
        projectTitle={project?.title}
      />

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        project={project}
        onProjectUpdate={handleProjectUpdate}
      />

      <ProjectSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        project={project}
        onUpdateProject={handleProjectUpdate}
      />

      {/* Top Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            {isEditingProjectTitle ? (
              <input
                type="text"
                value={projectTitleValue}
                onChange={(e) => setProjectTitleValue(e.target.value)}
                onBlur={() => {
                  setIsEditingProjectTitle(false)
                  updateProjectTitle(projectTitleValue)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setIsEditingProjectTitle(false)
                    updateProjectTitle(projectTitleValue)
                  } else if (e.key === 'Escape') {
                    setProjectTitleValue(project?.title || '')
                    setIsEditingProjectTitle(false)
                  }
                }}
                className="text-lg font-semibold text-gray-900 bg-white border border-primary-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
              />
            ) : (
              <h1 
                className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-primary-600 transition-colors"
                onClick={() => {
                  setIsEditingProjectTitle(true)
                  setProjectTitleValue(project?.title || '')
                }}
                title="Click to edit project title"
              >
                {project?.title}
              </h1>
            )}
            <p className="text-sm text-gray-500">
              {slides.length} slide{slides.length !== 1 ? 's' : ''} • {
                saving ? 'Saving...' : 
                lastSaved ? `Last saved ${formatDistanceToNow(lastSaved)} ago` : 
                'All changes saved'
              }
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {/* TODO: Re-enable these features in future versions */}
          {/* <button
            onClick={() => setShowVersionHistory(!showVersionHistory)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              showVersionHistory 
                ? 'bg-primary-600 text-white' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <SafeIcon icon={FiClock} className="w-4 h-4" />
            <span>History</span>
          </button>

          <button
            onClick={() => setShowAIAssistant(!showAIAssistant)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              showAIAssistant 
                ? 'bg-purple-600 text-white' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <SafeIcon icon={FiZap} className="w-4 h-4" />
            <span>AI Assistant</span>
          </button>

          <button
            onClick={() => setShowCollaboration(!showCollaboration)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              showCollaboration 
                ? 'bg-primary-600 text-white' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <SafeIcon icon={FiUsers} className="w-4 h-4" />
            <span>Collaborate</span>
          </button>

          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiDownload} className="w-4 h-4" />
            <span>Export</span>
          </button>

          {/* Undo/Redo buttons */}
          <button
            onClick={undo}
            disabled={undoStack.length === 0}
            className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Undo (Ctrl+Z / Cmd+Z)"
          >
            <SafeIcon icon={FiRotateCcw} className="w-4 h-4" />
          </button>

          <button
            onClick={redo}
            disabled={redoStack.length === 0}
            className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Redo (Ctrl+Y / Cmd+Shift+Z)"
          >
            <SafeIcon icon={FiRotateCw} className="w-4 h-4" />
          </button>

          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiSettings} className="w-4 h-4" />
            <span>Settings</span>
          </button>

          <button
            onClick={handlePreview}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiPlay} className="w-4 h-4" />
            <span>Present</span>
          </button>
          
          <button
            onClick={handleShare}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <SafeIcon icon={FiShare2} className="w-4 h-4" />
            <span>Share</span>
          </button>
        </div>
      </div>

      {/* Main Editor */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Slides */}
        <div className="w-64 bg-white border-r border-gray-200">
          <SlidePanel
            slides={slides}
            activeSlideIndex={activeSlideIndex}
            onSlideSelect={setActiveSlideIndex}
            onAddSlide={addSlide}
            onDeleteSlide={deleteSlide}
            onDuplicateSlide={duplicateSlide}
            onReorderSlides={reorderSlides}
            onUpdateSlideTitle={updateSlideTitle}
          />
        </div>

        {/* Center - Canvas */}
        <div className="flex-1 flex flex-col">
          <Toolbar 
            onAddElement={addElement}
            selectedElements={selectedElement ? [selectedElement] : []}
            onAlignElements={alignElements}
            gridEnabled={gridEnabled}
            onToggleGrid={setGridEnabled}
            snapEnabled={snapEnabled}
            onToggleSnap={setSnapEnabled}
          />
          <div className="flex-1 overflow-auto bg-gray-100 p-8">
            <Canvas
              slide={currentSlide}
              selectedElement={selectedElement}
              onSelectElement={setSelectedElement}
              onUpdateElement={updateElement}
              onDeleteElement={deleteElement}
              onDuplicateElement={duplicateElement}
              onElementRightClick={handleElementRightClick}
              onBringToFront={bringToFront}
              onSendToBack={sendToBack}
              onBringForward={bringForward}
              onSendBackward={sendBackward}
              onDragEnd={handleDragEnd}
              gridEnabled={gridEnabled}
              snapEnabled={snapEnabled}
            />
          </div>
        </div>

        {/* Right Sidebar - Properties, Collaboration, or Version History */}
        {showVersionHistory ? (
          <VersionHistory
            projectId={id}
            onRestoreVersion={handleRestoreVersion}
            onClose={() => setShowVersionHistory(false)}
          />
        ) : showCollaboration ? (
          <CollaborationPanel
            projectId={id}
            onClose={() => setShowCollaboration(false)}
          />
        ) : (
          <div className="w-80 bg-white border-l border-gray-200">
            <PropertiesPanel
              slide={currentSlide}
              selectedElement={selectedElement}
              onUpdateSlide={(updates) => updateSlide(activeSlideIndex, updates)}
              onUpdateElement={updateElement}
              onApplyBackgroundToAll={applyBackgroundToAll}
              projectTitle={project?.title}
              genre={project?.genre}
            />
          </div>
        )}
      </div>

      {/* AI Assistant */}
      <AIAssistant
        slide={currentSlide}
        onUpdateSlide={(updates) => updateSlide(activeSlideIndex, updates)}
        onAddElement={addElement}
        isOpen={showAIAssistant}
        onClose={() => setShowAIAssistant(false)}
      />

      {/* Comment System - Temporarily disabled
      <CommentSystem
        slideId={commentSystem.slideId}
        elementId={commentSystem.elementId}
        position={commentSystem.position}
        isVisible={commentSystem.isVisible}
        onClose={() => setCommentSystem({ isVisible: false, position: null, slideId: null, elementId: null })}
      />
      */}

      {/* Auto-save indicator */}
      {saving && (
        <div className="fixed bottom-4 left-4 bg-primary-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          <span>Saving changes...</span>
        </div>
      )}
    </div>
  )
}

export default SlideEditor